<template>
  <div class="optimize-contract-container">
    <!-- 返回按钮 -->
    <div class="back-button">
      <a-button type="text" @click="goBack">
        <template #icon>
          <LeftOutlined />
        </template>
        返回首页
      </a-button>
    </div>

    <!-- 步骤条 -->
    <div class="steps-container">
      <a-steps :current="currentStep" :items="steps"></a-steps>
    </div>

    <!-- 步骤内容区域 -->
    <div class="steps-content">
      <!-- 第一步：选择模板格式 -->
      <div v-if="currentStep === 0" class="step-content">
        <h2>选择模板格式</h2>
        <div class="template-section">
          <div class="template-title">模板</div>
          <div v-if="loading" class="loading-state">
            <a-spin size="large" />
            <p>正在加载模板...</p>
          </div>
          <div v-else-if="templates.length === 0" class="empty-state">
            <p>暂无可用模板</p>
          </div>
          <div v-else class="template-carousel">
            <div class="carousel-container">
              <button class="arrow left-arrow" @click="prevTemplate" :disabled="currentTemplateIndex === 0">
                <LeftOutlined />
              </button>
              
              <div class="carousel-wrapper">
                <div class="carousel-inner" :style="{ transform: `translateX(-${currentTemplateIndex * 100}%)` }">
                  <div class="template-group" v-for="(group, groupIndex) in templateGroups" :key="groupIndex">
                    <div class="template-cards">
                      <div 
                        class="template-card" 
                        v-for="template in group" 
                        :key="template.id"
                        :class="{ 'active': selectedTemplate?.id === template.id }"
                        @click="selectTemplate(template)"
                      >
                        <div class="template-image">
                          <img 
                            :src="getFullImageUrl(template.id)" 
                            :alt="template.name" 
                            @error="handleImageError"
                            @load="handleImageLoad"
                            :class="{ 'loaded': imageLoaded[template.id] }"
                          />
                          <div class="template-overlay">
                            <div class="preview-button" @click.stop="previewImage(template)">
                              <EyeOutlined style="font-size: 24px; color: white;" />
                              <span class="preview-text">点击预览</span>
                            </div>
                          </div>
                          <div v-if="!imageLoaded[template.id]" class="image-loading">
                            <LoadingOutlined style="font-size: 24px; color: #1890ff;" />
                          </div>
                        </div>
                        <div class="template-info">
                          <div class="template-name">{{ template.name }}</div>
                          <div class="template-description">{{ template.description }}</div>
                          <div class="template-category">{{ template.category }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button class="arrow right-arrow" @click="nextTemplate" :disabled="currentTemplateIndex >= templateGroups.length - 1">
                <RightOutlined />
              </button>
            </div>
            
            <!-- 指示器 -->
            <div class="carousel-indicators">
              <span 
                v-for="(group, index) in templateGroups" 
                :key="index"
                class="indicator"
                :class="{ 'active': currentTemplateIndex === index }"
                @click="currentTemplateIndex = index"
              ></span>
            </div>
          </div>
        </div>
        <div class="step-actions">
          <a-button type="primary" :disabled="!selectedTemplate" @click="nextStep">
            确定并下一步
          </a-button>
        </div>
      </div>

      <!-- 第二步：粘贴内容 -->
      <div v-if="currentStep === 1" class="step-content step-paste-content">
        <h2>粘贴合同内容</h2>
        <div class="paste-section">
          <div class="paste-container">
            <!-- 左侧：输入框 -->
            <div class="input-section">
              <h3 class="section-title">合同内容输入</h3>
              <a-textarea
                v-model:value="pasteContent"
                placeholder="请在此处粘贴合同内容..."
                :rows="14"
                show-count
                :maxlength="8000"
                class="content-textarea"
                :bordered="false"
              />
            </div>

            <!-- 右侧：模板预览 -->
            <div class="preview-section">
              <h3 class="section-title">模板预览</h3>
              <div class="preview-container">
                <div v-if="selectedTemplate" class="template-preview">
                  <div class="preview-image-wrapper">
                    <img 
                      :src="getFullImageUrl(selectedTemplate.id)" 
                      :alt="selectedTemplate.name"
                      class="preview-template-image"
                      @load="handlePreviewImageLoad"
                    />
                    <div v-if="!previewImageLoaded" class="preview-loading">
                      <a-spin size="large" />
                      <p>加载预览中...</p>
                    </div>
                  </div>
                  <!-- 已移除预览信息区域 -->
                </div>
                <div v-else class="no-template-selected">
                  <FileTextOutlined style="font-size: 48px; color: #ccc; margin-bottom: 16px;" />
                  <p>请先选择一个模板</p>
                  <a-button type="primary" @click="currentStep = 0" style="margin-top: 16px;">
                    返回选择模板
                  </a-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="step-actions">
          <a-button @click="prevStep">上一步</a-button>
          <a-button type="primary" :disabled="!pasteContent.trim() || !selectedTemplate" @click="submitToAI" :loading="submitting">
            {{ submitting ? '提交中...' : '确定并生成' }}
          </a-button>
        </div>
      </div>

      <!-- 其他步骤保持不变 -->
      <!-- 第三步：等待处理 -->
      <div v-if="currentStep === 2" class="step-content">
        <h2>正在生成文档</h2>
        <div class="loading-section">
          <div class="loading-content">
            <div class="loading-icon">
              <LoadingOutlined style="font-size: 48px; color: #1890ff;" />
            </div>
            <p style="margin-top: 16px;">AI正在智能生成文档，请耐心等待...</p>
            <p class="loading-tip">这通常需要2-3分钟时间，请勿关闭页面</p>
          </div>
        </div>
        <div class="step-actions" v-if="showCancelButton">
          <a-button @click="cancelProcessing">取消处理</a-button>
        </div>
      </div>

      <!-- 第四步：完成 -->
      <div v-if="currentStep === 3" class="step-content">
        <h2>文档生成完成</h2>
        <div class="result-section">
          <div class="success-icon">
            <SmileOutlined style="font-size: 64px; color: #52c41a;" />
          </div>
          <p class="success-text">文档已成功生成！</p>
          <p class="success-desc">您可以选择下载生成后的合同文件或返回首页</p>
          
          <div class="result-actions">
            <a-button type="primary" size="large" @click="downloadContract">
              <template #icon>
                <DownloadOutlined />
              </template>
              下载合同
            </a-button>
            <a-button size="large" @click="resetToFirstStep" style="margin-left: 16px;">
              重新选择
            </a-button>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div v-if="currentStep === 4" class="step-content">
        <h2>处理失败</h2>
        <div class="error-section">
          <div class="error-icon">
            <ExclamationCircleOutlined style="font-size: 64px; color: #ff4d4f;" />
          </div>
          <p class="error-text">文档生成处理失败</p>
          <p class="error-desc">{{ errorMessage }}</p>
          
          <div class="error-actions">
            <a-button type="primary" size="large" @click="retryProcessing">
              重新尝试
            </a-button>
            <a-button size="large" @click="goBack" style="margin-left: 16px;">
              返回首页
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览模态框 -->
    <a-modal
      v-model:open="previewVisible"
      :footer="null"
      width="80%"
      style="top: 20px"
      @cancel="handlePreviewCancel"
      class="image-preview-modal"
    >
      <template #title>
        <div class="preview-title">
          <EyeOutlined style="margin-right: 8px;" />
          {{ previewTemplate?.name || '模板预览' }}
        </div>
      </template>
      <div class="preview-content">
        <div class="preview-image-container">
          <img 
            :src="previewImageUrl" 
            :alt="previewTemplate?.name" 
            class="preview-image"
            @load="handlePreviewImageLoad"
          />
          <div v-if="!previewImageLoaded" class="preview-loading">
            <a-spin size="large" />
            <p>加载图片中...</p>
          </div>
        </div>
        <div v-if="previewTemplate" class="preview-info">
          <h3 class="preview-template-name">{{ previewTemplate.name }}</h3>
          <p class="preview-template-description">{{ previewTemplate.description }}</p>
          <div class="preview-template-category">
            <span class="category-tag">{{ previewTemplate.category }}</span>
          </div>
          <div class="preview-actions">
            <a-button 
              type="primary" 
              @click="selectPreviewTemplate"
              :class="{ 'selected': selectedTemplate?.id === previewTemplate.id }"
            >
              {{ selectedTemplate?.id === previewTemplate.id ? '已选择' : '选择此模板' }}
            </a-button>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
// 脚本部分保持不变
import { ref, onMounted, computed, onUnmounted, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { 
  LeftOutlined, 
  RightOutlined,
  LoadingOutlined, 
  SmileOutlined,
  DownloadOutlined,
  FileTextOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import config from '@/config' // 导入全局配置

const router = useRouter()
const API_BASE_URL = config.API_BASE_URL // 获取API基础地址

// 步骤数据
const currentStep = ref(0)
const pasteContent = ref('')
const selectedTemplate = ref(null)
const currentTemplateIndex = ref(0)
const loading = ref(false)
const submitting = ref(false)
const showCancelButton = ref(true)
const errorMessage = ref('')
const imageLoaded = reactive({}) // 跟踪图片加载状态
const isProcessing = ref(false) // 新增处理状态标志

// 图片预览相关状态
const previewVisible = ref(false)
const previewTemplate = ref(null)
const previewImageUrl = ref('')
const previewImageLoaded = ref(false)

//轮询查询
const taskId = ref('')
const pollingInterval = ref(null)
const maxPollingTime = 300000 // 最大轮询时间5分钟
const pollingStartTime = ref(0)

//标志防重复
const hasShownSuccess = ref(false)

// 模板数据
const templates = ref([])

const optimizedFilePath = ref('')

// 定时器
let processingTimer = null

// 步骤配置
const steps = computed(() => {
  return [
    {
      title: '选择模板',
      icon: h(FileTextOutlined),
    },
    {
      title: '粘贴内容',
      icon: h(FileTextOutlined),
    },
    {
      title: '等待生成',
      // 使用计算属性来动态决定图标
      icon: (currentStep.value === 2 && isProcessing.value) ? h(LoadingOutlined) : h(ClockCircleOutlined),
    },
    {
      title: '完成',
      icon: h(SmileOutlined),
    },
  ]
})

// 计算属性：将模板分组，每组4个
const templateGroups = computed(() => {
  const groups = []
  for (let i = 0; i < templates.value.length; i += 4) {
    groups.push(templates.value.slice(i, i + 4))
  }
  return groups
})

// 获取模板列表
const fetchTemplates = async () => {
  loading.value = true;
  try {
    // 使用全局配置的API地址
    const response = await fetch(`${API_BASE_URL}/templates/`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    templates.value = data; // 数据格式含id：[{id:1,...}, {id:2,...}]

  } catch (error) {
    console.error('获取模板失败:', error);
    message.error('获取模板失败，请重试');
    templates.value = [];
  } finally {
    loading.value = false;
  }
};

// 基于模板id获取图片URL
const getFullImageUrl = (templateId) => {
  // 兜底：id无效时用默认图
  if (!templateId || isNaN(Number(templateId))) {
    return '/images/default-template.jpg';
  }
  // 使用全局配置的API地址
  return `${API_BASE_URL}/templates/${templateId}/preview`;
};

// 图片加载成功处理
const handleImageLoad = (event) => {
  const img = event.target;
  // 1. 拆分URL并过滤空值
  const urlParts = img.src.split('/').filter(part => part);
  // 2. 从URL倒数第二段提取templateId（适配 /templates/{id}/preview 结构）
  const templateId = urlParts[urlParts.length - 2]; 
  // 3. 验证ID有效（避免非数字ID导致的异常）
  if (templateId && !isNaN(Number(templateId))) {
    imageLoaded[templateId] = true;
  }
}

// 预览图片
const previewImage = (template) => {
  previewTemplate.value = template;
  previewImageUrl.value = getFullImageUrl(template.id);
  previewVisible.value = true;
  previewImageLoaded.value = false;
}

// 预览图片加载完成
const handlePreviewImageLoad = () => {
  previewImageLoaded.value = true;
}

// 关闭预览
const handlePreviewCancel = () => {
  previewVisible.value = false;
  previewTemplate.value = null;
  previewImageUrl.value = '';
}

// 选择预览的模板
const selectPreviewTemplate = () => {
  if (previewTemplate.value) {
    selectedTemplate.value = previewTemplate.value;
    previewVisible.value = false;
    message.success(`已选择模板：${previewTemplate.value.name}`);
  }
}

// 选择模板
const selectTemplate = (template) => {
  selectedTemplate.value = template
}

// 上一个模板组
const prevTemplate = () => {
  if (currentTemplateIndex.value > 0) {
    currentTemplateIndex.value--
  }
}

// 下一个模板组
const nextTemplate = () => {
  if (currentTemplateIndex.value < templateGroups.value.length - 1) {
    currentTemplateIndex.value++
  }
}

// 下一步
const nextStep = () => {
  currentStep.value++
}

// 上一步
const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// 修改 submitToAI 函数
const submitToAI = async () => {
  if (!selectedTemplate.value || !pasteContent.value.trim()) {
    message.error('请先选择模板并填写内容')
    return
  }

  submitting.value = true
  isProcessing.value = true

  try {
    const requestData = {
      template_id: selectedTemplate.value.id,
      text: pasteContent.value.trim()
    }

    const response = await fetch(`${API_BASE_URL}/templates/fill_personal_info/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    })

	console.log('服务器响应状态:', response.status);

	try {
      const responseData = await response.clone().json(); // clone()避免流被消费
      console.log('服务器完整响应内容:', responseData);
    } catch (e) {
      const responseText = await response.clone().text();
      console.log('服务器响应文本:', responseText);
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    taskId.value = result.task_id
    console.log('服务器轮询taskid:', taskId.value);

    // 提交成功，进入等待状态
    currentStep.value = 2
    pollingStartTime.value = Date.now()
    
    // 开始轮询
    startPolling()

  } catch (error) {
    console.error('提交失败:', error)
    message.error('提交失败，请重试')
    errorMessage.value = error.message || '网络请求失败，请检查网络连接后重试'
    currentStep.value = 4
    isProcessing.value = false //重置状态
  } finally {
    submitting.value = false
  }
}

// 开始轮询
const startPolling = () => {
  hasShownSuccess.value = false
  // 立即执行第一次查询
  checkTaskStatus()
  
  // 设置轮询间隔，每3秒查询一次
  pollingInterval.value = setInterval(() => {
    checkTaskStatus()
  }, 3000)
}

// 检查任务状态
const checkTaskStatus = async () => {
	if (!pollingInterval.value) {
    return
  }
  try {
    const response = await fetch(`${API_BASE_URL}/templates/fill_personal_info/status/${taskId.value}`)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    console.log('轮询回复:', response)
    console.log('轮询结果:', result)
	console.log('处理结果:', result.status)
    if (result.status === 'done') {

      // 任务完成，获取结果
      await fetchTaskResult()
    } else if (result.status === 'error') {
      // 任务出错，停止轮询并显示错误
      stopPolling()
      errorMessage.value = 'AI处理过程中发生错误，请重试'
      currentStep.value = 4
    } else if (result.status === 'running') {
      // 任务仍在运行，继续等待
      console.log('任务处理中...')
    }
    
    // 检查是否超时
    if (Date.now() - pollingStartTime.value > maxPollingTime) {
      stopPolling()
      errorMessage.value = '处理超时，请重新尝试'
      currentStep.value = 4
    }
    
  } catch (error) {
    console.error('查询任务状态失败:', error)
    // 不立即显示错误，继续重试（可能是网络暂时问题）
  }
}

// 获取任务结果
const fetchTaskResult = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/templates/fill_personal_info/result/${taskId.value}`)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    console.log('任务结果:', result)
    
    // 从结果中获取文件路径
    if (result.file_name) {
      optimizedFilePath.value = result.file_name
      console.log('获取的文件路径:', optimizedFilePath.value)
      // 任务完成，停止轮询并进入完成步骤
      stopPolling()
      currentStep.value = 3
      isProcessing.value = false // 处理完成，重置状态
      if (!hasShownSuccess.value) {
        message.success('文档生成成功！')
        hasShownSuccess.value = true
      }
    } else {
      throw new Error('未找到文档的文件路径')
    }
    
  } catch (error) {
    console.error('获取任务结果失败:', error)
    stopPolling()
    errorMessage.value = '获取文档结果失败，请重试'
    currentStep.value = 4
    message.error('获取文档结果失败')
	isProcessing.value = false // 出错时重置状态
  }
}
// 停止轮询
const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

// 修改重试处理函数
const retryProcessing = () => {
  stopPolling()
  currentStep.value = 1
  isProcessing.value = false // 重置处理状态
}

// 修改取消处理函数
const cancelProcessing = () => {
  stopPolling()
  message.info('已取消处理')
  currentStep.value = 1
  isProcessing.value = false // 重置处理状态
}

// 组件卸载时清理定时器
onUnmounted(() => {
  stopPolling()
  if (processingTimer) {
    clearTimeout(processingTimer)
  }
})

const downloadContract = async () => {
  if (!optimizedFilePath.value) {
    message.error('没有可下载的文件')
    return
  }

  try {
    message.loading('正在准备下载...', 0)
    
    // 从文件路径中提取文件名
    const fileName = optimizedFilePath.value
    
    // 构建下载URL
    const downloadUrl = `${API_BASE_URL}/download/?file_name=${encodeURIComponent(fileName)}`
    
    console.log('下载URL:', downloadUrl)
    
    // 使用fetch下载文件
    const response = await fetch(downloadUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/octet-stream',
      }
    })

    if (!response.ok) {
      throw new Error(`下载失败: ${response.status} ${response.statusText}`)
    }

    // 获取Blob数据
    const blob = await response.blob()
    
    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // 从Content-Disposition头获取文件名，如果没有则使用默认名
    const contentDisposition = response.headers.get('Content-Disposition')
    let downloadFileName = fileName
    
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i)
      if (fileNameMatch && fileNameMatch[1]) {
        downloadFileName = fileNameMatch[1]
      }
    }
    
    link.download = downloadFileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // 清理URL对象
    window.URL.revokeObjectURL(url)
    
    message.destroy()
    message.success('文件下载成功！')
    
  } catch (error) {
    console.error('下载失败:', error)
    message.destroy()
    message.error(`下载失败: ${error.message}`)
    
    // 备用方案：直接打开下载链接
    const fileName = optimizedFilePath.value.split('/').pop() || 'optimized_contract.docx'
    const downloadUrl = `${API_BASE_URL}/templates/download/?file_name=${encodeURIComponent(fileName)}`
    window.open(downloadUrl, '_blank')
  }
}

// 返回首页
const goBack = () => {
  router.push('/')
}

//重新选择
const resetToFirstStep = () => {
  currentStep.value = 0
  // 可选：清空之前的选择和内容
  selectedTemplate.value = null
  pasteContent.value = ''
  optimizedFilePath.value = ''
  errorMessage.value = ''
  
  // 重置轮询状态
  stopPolling()
  hasShownSuccess.value = false
  taskId.value = ''
  
  message.info('已重置，请重新选择模板')
}

// 组件挂载时获取模板
onMounted(() => {
  fetchTemplates()
})

// 组件卸载时清理定时器
onUnmounted(() => {
  if (processingTimer) {
    clearTimeout(processingTimer)
  }
})

// 导入所需的图标组件
import { h } from 'vue'
</script>

<style scoped>
.optimize-contract-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;
  min-height: 100vh;
}

.back-button {
  margin-bottom: 30px;
}

.steps-container {
  margin-bottom: 40px;
}

.step-content {
  text-align: center;
  padding: 20px 0;
}

.step-paste-content {
  padding: 20px 0;
}

.step-content h2 {
  margin-bottom: 30px;
  color: #333;
  font-size: 28px;
  font-weight: 600;
}

.template-section {
  max-width: 1000px;
  margin: 0 auto 30px;
  text-align: left;
}

.template-title {
  font-family: "黑体", "SimHei", sans-serif;
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.template-carousel {
  position: relative;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 40px 0;
  color: #666;
}

.carousel-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.carousel-wrapper {
  flex: 1;
  overflow: hidden;
}

.carousel-inner {
  display: flex;
  transition: transform 0.3s ease;
}

.template-group {
  flex: 0 0 100%;
  display: flex;
  justify-content: center;
}

.template-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 1000px;
}

.template-card {
  background: #fff;
  border: 2px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.template-card:hover {
  border-color: #1890ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.template-card.active {
  border-color: #1890ff;
  background: #f0f8ff;
}

.template-image {
  position: relative;
  width: 100%;
  height: 200px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.template-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease;
  opacity: 0;
  border: none;
}

.template-image img.loaded {
  opacity: 1;
}

.template-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.template-card:hover .template-overlay {
  opacity: 1;
}

.preview-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.2s ease;
}

.preview-button:hover {
  transform: scale(1.1);
}

.preview-text {
  margin-top: 8px;
  font-size: 12px;
}

.image-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.template-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.template-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
  font-size: 16px;
}

.template-description {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 4px;
  flex: 1;
}

.template-category {
  font-size: 12px;
  color: #999;
  margin-top: auto;
}

.arrow {
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.arrow:hover:not(:disabled) {
  background: #1890ff;
  border-color: #1890ff;
  color: #fff;
}

.arrow:disabled {
  background: #f5f5f5;
  border-color: #d9d9d9;
  color: #ccc;
  cursor: not-allowed;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d9d9d9;
  cursor: pointer;
  transition: background 0.3s ease;
}

.indicator.active {
  background: #1890ff;
}

/* 修改粘贴内容区域的样式 */
.paste-section {
  width: 100%;
  margin: 0 auto 30px;
}

.paste-container {
  display: flex;
  gap: 24px;
  align-items: stretch;
  min-height: 500px; /* 降低整体高度 */
  max-width: 1100px;
  margin: 0 auto;
}

.input-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.preview-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  text-align: left;
  padding-bottom: 10px;
  border-bottom: 2px solid #1890ff;
}

.content-textarea {
  flex: 1;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.5;
  background: white;
  border-radius: 6px;
  padding: 12px;
  resize: vertical;
}

:deep(.content-textarea .ant-input) {
  font-size: 14px;
  line-height: 1.5;
  min-height: 300px !important; /* 降低文本区域高度 */
  resize: vertical;
}

.preview-container {
  flex: 1;
  border: none;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: white;
}

.template-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.preview-image-wrapper {
  flex: 1;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  position: relative;
  min-height: 300px; /* 降低预览图片区域高度 */
  padding: 8px;
  border: none;
}

.preview-template-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  border: none;
}

/* 已移除 .preview-info 相关样式 */

.no-template-selected {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 40px 20px;
  text-align: center;
}

.no-template-selected p {
  margin: 8px 0;
  font-size: 14px;
}

.step-actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.step-actions .ant-btn {
  margin: 0 10px;
  padding: 0 24px;
  height: 36px;
  font-size: 14px;
}

.loading-content {
  padding: 40px 0;
}

.loading-icon {
  margin-bottom: 16px;
}

.loading-tip {
  color: #999;
  font-size: 14px;
  margin-top: 8px;
}

.success-icon,
.error-icon {
  margin-bottom: 24px;
}

.success-text {
  font-size: 22px;
  color: #52c41a;
  margin-bottom: 10px;
  font-weight: 600;
}

.error-text {
  font-size: 22px;
  color: #ff4d4f;
  margin-bottom: 10px;
  font-weight: 600;
}

.success-desc,
.error-desc {
  color: #666;
  margin-bottom: 30px;
  font-size: 15px;
}

.result-actions,
.error-actions {
  margin-top: 30px;
}

/* 图片预览模态框样式 */
:deep(.image-preview-modal .ant-modal-body) {
  padding: 0;
}

.preview-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
}

.preview-content {
  display: flex;
  height: 500px;
}

.preview-image-container {
  flex: 1;
  position: relative;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  border: none;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border: none;
}

.preview-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
}

.preview-info {
  width: 280px;
  padding: 20px;
  background: #fafafa;
  border-left: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}

.preview-template-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.preview-template-description {
  color: #666;
  line-height: 1.5;
  margin-bottom: 14px;
  flex: 1;
  font-size: 14px;
}

.preview-template-category {
  margin-bottom: 20px;
}

.category-tag {
  display: inline-block;
  padding: 4px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #91d5ff;
}

.preview-actions {
  margin-top: auto;
}

.preview-actions .ant-btn.selected {
  background: #52c41a;
  border-color: #52c41a;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .optimize-contract-container {
    max-width: 100%;
    padding: 15px;
  }
  
  .paste-container {
    max-width: 100%;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .optimize-contract-container {
    padding: 10px;
  }
  
  .step-content {
    padding: 15px 0;
  }
  
  .step-content h2 {
    font-size: 24px;
  }
  
  .template-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .template-image {
    height: 150px;
  }
  
  .carousel-container {
    gap: 10px;
  }
  
  .arrow {
    width: 32px;
    height: 32px;
  }
  
  .preview-content {
    flex-direction: column;
    height: auto;
  }
  
  .preview-info {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e8e8e8;
  }

  /* 移动端粘贴内容布局 */
  .paste-container {
    flex-direction: column;
    min-height: auto;
    gap: 20px;
  }
  
  .input-section,
  .preview-section {
    min-height: 350px; /* 移动端也降低高度 */
    padding: 16px;
  }
  
  .content-textarea {
    min-height: 250px; /* 移动端降低文本区域高度 */
  }
}

@media (max-width: 480px) {
  .template-cards {
    grid-template-columns: 1fr;
  }
  
  .template-image {
    height: 160px;
  }
  
  .step-content h2 {
    font-size: 20px;
  }
  
  .input-section,
  .preview-section {
    padding: 12px;
  }
}
</style>